/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { CellPadding, TableFooterRendererContext } from '.';
import { FormContext } from '../hooks/UNSAFE_useFormContext';
import { classNames } from '../utils/UNSAFE_classNames';
import {
  getFormTextAlign,
  getHeaderFooterCellClassArray
} from '../PRIVATE_Table/utils/TableThemeUtils';

/**
 * Props for the TableDisplayFooterCell Component
 */
export type TableDisplayFooterCellProps<C> = {
  tableId: string;
  columnKey: C;
  columnIndex: number;
  isFinalColumnIndex: boolean;
  isFirstLogicalColumnIndex: boolean;
  isFinalLogicalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  isSticky: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  footerText?: string;
  stickyBottomOffset?: number;
  renderer?: (context: TableFooterRendererContext<C>) => ComponentChildren;
  padding?: CellPadding;
  horizontalAlignment?: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment?: 'top' | 'center' | 'bottom';
};

function _defaultFooterCellRenderer<C>({ footerText }: TableFooterRendererContext<C>) {
  return footerText != null ? footerText : undefined;
}

/**
 * The internal component used to render a single cell in a TableDisplayFooterRow.
 */
export function TableDisplayFooterCell<C>({
  tableId,
  columnKey,
  columnIndex,
  isFinalColumnIndex,
  isFirstLogicalColumnIndex,
  isFinalLogicalColumnIndex,
  hasVerticalGridlines,
  isSticky,
  stickyBottomOffset = 0,
  footerText,
  renderer = _defaultFooterCellRenderer,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isRtl,
  isHighContrast,
  padding,
  horizontalAlignment = 'start',
  verticalAlignment = 'center'
}: TableDisplayFooterCellProps<C>) {
  const cellClasses = classNames(
    getHeaderFooterCellClassArray({
      isHeader: false,
      isFirstColumnIndex: columnIndex === 0 || isFirstLogicalColumnIndex,
      isFinalColumnIndex: isFinalColumnIndex || isFinalLogicalColumnIndex,
      hasVerticalGridlines,
      isSticky,
      hasStartEdge,
      hasEndEdge,
      isStickyStartEdge,
      isStickyEndEdge,
      isRtl,
      horizontalAlignment,
      verticalAlignment,
      isHighContrast,
      padding
    })
  );

  const footerCellRendererProps = {
    key: columnKey,
    footerText: footerText
  };

  return (
    <td
      aria-colindex={columnIndex + 1}
      class={cellClasses}
      style={`bottom:${stickyBottomOffset}px;`}
      role={'cell'}
      data-oj-cell-type={'footer'}
      data-oj-column-key={columnKey}
      data-oj-table-focusable={tableId}
      data-oj-table-footer={tableId}>
      <FormContext.Provider value={{ textAlign: getFormTextAlign(horizontalAlignment, isRtl) }}>
        {renderer(footerCellRendererProps)}
      </FormContext.Provider>
    </td>
  );
}
